import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { ColorBlock, SectionHeading, BodyText, BLOCKS, P, PD, A } from './BlogShared.jsx';

/* ─── Glassmorphic 3D SVG: translucent containers, depth, gradient spheres ─── */
const InventorySVG = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) el.classList.add('inv-active'); },
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <Box ref={ref} sx={{ width: '100%', maxWidth: 460, mx: 'auto' }}>
      <style>{`
        .inv-bar { transform: scaleY(0); transform-origin: bottom; transition: transform 0.8s cubic-bezier(.16,1,.3,1); }
        .inv-active .inv-bar:nth-child(1) { transform: scaleY(1); transition-delay: 0.2s; }
        .inv-active .inv-bar:nth-child(2) { transform: scaleY(1); transition-delay: 0.35s; }
        .inv-active .inv-bar:nth-child(3) { transform: scaleY(1); transition-delay: 0.5s; }
        .inv-active .inv-bar:nth-child(4) { transform: scaleY(1); transition-delay: 0.65s; }
        .inv-active .inv-bar:nth-child(5) { transform: scaleY(1); transition-delay: 0.8s; }
        .inv-fade { opacity: 0; transition: opacity 0.7s ease; }
        .inv-active .inv-fade { opacity: 1; transition-delay: 0.4s; }
        .inv-float { animation: invFloat 4s ease-in-out infinite; }
        @keyframes invFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
      `}</style>
      <svg viewBox="0 0 460 340" fill="none" style={{ width: '100%', display: 'block' }}>
        <defs>
          {/* Glassmorphic gradients */}
          <linearGradient id="glassPanel" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="white" stopOpacity="0.7" />
            <stop offset="100%" stopColor="white" stopOpacity="0.35" />
          </linearGradient>
          <linearGradient id="sphereP" x1="0.2" y1="0" x2="0.8" y2="1">
            <stop offset="0%" stopColor={PD} />
            <stop offset="50%" stopColor={P} />
            <stop offset="100%" stopColor={A} />
          </linearGradient>
          <linearGradient id="sphereA" x1="0.3" y1="0" x2="0.7" y2="1">
            <stop offset="0%" stopColor={A} />
            <stop offset="100%" stopColor={P} />
          </linearGradient>
          <linearGradient id="cylGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={P} stopOpacity="0.3" />
            <stop offset="50%" stopColor={P} stopOpacity="0.12" />
            <stop offset="100%" stopColor={A} stopOpacity="0.2" />
          </linearGradient>
          <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={P} />
            <stop offset="100%" stopColor={A} />
          </linearGradient>
          <filter id="glassBlur">
            <feGaussianBlur in="SourceGraphic" stdDeviation="1" />
          </filter>
          <filter id="softShadow">
            <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor={P} floodOpacity="0.15" />
          </filter>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" />
            <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* Background decorative shapes */}
        <circle cx="60" cy="280" r="40" fill={`${P}05`} />
        <circle cx="400" cy="60" r="30" fill={`${A}05`} />

        {/* ═══ Left: Glass shelf panel ═══ */}
        <g filter="url(#softShadow)">
          <rect x="20" y="30" width="200" height="280" rx="20" fill="url(#glassPanel)" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" />
        </g>
        {/* Frosted edge highlight */}
        <rect x="20" y="30" width="200" height="4" rx="2" fill="rgba(255,255,255,0.8)" />

        {/* Glass shelf dividers */}
        <line x1="35" y1="110" x2="205" y2="110" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" />
        <line x1="35" y1="185" x2="205" y2="185" strokeWidth="1.5" stroke="rgba(255,255,255,0.5)" />
        <line x1="35" y1="250" x2="205" y2="250" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" />

        {/* Shelf 1: 3D cylinders (stock containers) */}
        <g className="inv-fade inv-float">
          <ellipse cx="60" cy="100" rx="18" ry="6" fill={`${P}20`} />
          <rect x="42" y="55" width="36" height="45" rx="0" fill="url(#cylGrad)" />
          <ellipse cx="60" cy="55" rx="18" ry="6" fill={`${P}35`} stroke={`${P}40`} strokeWidth="0.8" />
          <ellipse cx="60" cy="55" rx="10" ry="3" fill="rgba(255,255,255,0.5)" />
        </g>
        <g className="inv-fade" style={{ animationDelay: '0.5s' }}>
          <ellipse cx="110" cy="100" rx="14" ry="5" fill={`${A}18`} />
          <rect x="96" y="65" width="28" height="35" rx="0" fill={`${A}15`} />
          <ellipse cx="110" cy="65" rx="14" ry="5" fill={`${A}30`} stroke={`${A}35`} strokeWidth="0.8" />
          <ellipse cx="110" cy="65" rx="7" ry="2.5" fill="rgba(255,255,255,0.4)" />
        </g>
        <g className="inv-fade">
          <ellipse cx="160" cy="100" rx="20" ry="6" fill={`${P}15`} />
          <rect x="140" y="50" width="40" height="50" rx="0" fill={`${P}12`} />
          <ellipse cx="160" cy="50" rx="20" ry="6" fill={`${P}28`} stroke={`${P}30`} strokeWidth="0.8" />
          <ellipse cx="160" cy="50" rx="11" ry="3" fill="rgba(255,255,255,0.45)" />
        </g>

        {/* Shelf 2: Gradient spheres (ingredients) */}
        <g className="inv-fade inv-float" style={{ animationDelay: '1s' }}>
          <circle cx="55" cy="148" r="18" fill="url(#sphereP)" opacity="0.85" />
          <ellipse cx="50" cy="140" rx="7" ry="5" fill="rgba(255,255,255,0.4)" />
        </g>
        <g className="inv-fade">
          <circle cx="100" cy="152" r="14" fill="url(#sphereA)" opacity="0.75" />
          <ellipse cx="96" cy="146" rx="5" ry="4" fill="rgba(255,255,255,0.35)" />
        </g>
        <g className="inv-fade inv-float" style={{ animationDelay: '2s' }}>
          <circle cx="140" cy="148" r="16" fill="url(#sphereP)" opacity="0.7" />
          <ellipse cx="136" cy="141" rx="6" ry="4.5" fill="rgba(255,255,255,0.4)" />
        </g>
        <g className="inv-fade">
          <circle cx="180" cy="153" r="12" fill="url(#sphereA)" opacity="0.65" />
          <ellipse cx="177" cy="148" rx="4.5" ry="3" fill="rgba(255,255,255,0.3)" />
        </g>

        {/* Shelf 3: Glass cubes (smaller items) */}
        <g className="inv-fade">
          <rect x="40" y="200" width="30" height="30" rx="6" fill="url(#glassPanel)" stroke={`${P}25`} strokeWidth="1" transform="rotate(-5 55 215)" />
          <rect x="43" y="203" width="10" height="10" rx="2" fill="rgba(255,255,255,0.6)" />
        </g>
        <g className="inv-fade">
          <rect x="82" y="198" width="34" height="34" rx="7" fill="url(#glassPanel)" stroke={`${A}20`} strokeWidth="1" />
          <rect x="86" y="201" width="11" height="11" rx="2" fill="rgba(255,255,255,0.5)" />
        </g>
        <g className="inv-fade">
          <rect x="130" y="202" width="28" height="28" rx="6" fill="url(#glassPanel)" stroke={`${P}20`} strokeWidth="1" transform="rotate(3 144 216)" />
          <rect x="133" y="205" width="9" height="9" rx="2" fill="rgba(255,255,255,0.55)" />
        </g>
        <g className="inv-fade">
          <rect x="170" y="200" width="26" height="26" rx="5" fill="url(#glassPanel)" stroke={`${P}15`} strokeWidth="1" />
        </g>

        {/* Shelf 4: Small capsules */}
        <g className="inv-fade">
          <rect x="42" y="260" width="22" height="14" rx="7" fill={`${P}20`} stroke={`${P}30`} strokeWidth="0.8" />
          <rect x="72" y="262" width="20" height="12" rx="6" fill={`${A}18`} stroke={`${A}25`} strokeWidth="0.8" />
          <rect x="100" y="261" width="24" height="13" rx="6.5" fill={`${P}15`} stroke={`${P}22`} strokeWidth="0.8" />
          <rect x="132" y="260" width="30" height="14" rx="7" fill={`${A}12`} stroke={`${A}18`} strokeWidth="0.8" />
          <rect x="170" y="262" width="18" height="12" rx="6" fill={`${P}18`} stroke={`${P}25`} strokeWidth="0.8" />
        </g>

        {/* ═══ Right: Analytics glass panel ═══ */}
        <g filter="url(#softShadow)">
          <rect x="240" y="30" width="200" height="280" rx="20" fill="url(#glassPanel)" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" />
        </g>
        <rect x="240" y="30" width="200" height="4" rx="2" fill="rgba(255,255,255,0.8)" />

        {/* Glassmorphic bar chart */}
        <g>
          <rect className="inv-bar" x="264" y="120" width="22" height="110" rx="11" fill="url(#barGrad)" opacity="0.85" />
          <rect className="inv-bar" x="296" y="150" width="22" height="80" rx="11" fill="url(#barGrad)" opacity="0.7" />
          <rect className="inv-bar" x="328" y="100" width="22" height="130" rx="11" fill="url(#barGrad)" opacity="0.9" />
          <rect className="inv-bar" x="360" y="160" width="22" height="70" rx="11" fill="url(#barGrad)" opacity="0.65" />
          <rect className="inv-bar" x="392" y="130" width="22" height="100" rx="11" fill="url(#barGrad)" opacity="0.8" />
        </g>
        {/* Glass bar highlights */}
        <rect className="inv-fade" x="268" y="125" width="6" height="40" rx="3" fill="rgba(255,255,255,0.35)" />
        <rect className="inv-fade" x="332" y="105" width="6" height="45" rx="3" fill="rgba(255,255,255,0.3)" />
        <rect className="inv-fade" x="396" y="135" width="6" height="35" rx="3" fill="rgba(255,255,255,0.3)" />

        {/* Baseline glow */}
        <line x1="255" y1="230" x2="420" y2="230" stroke={`${P}15`} strokeWidth="1.5" />

        {/* Trend orb with glow */}
        <path className="inv-fade" d="M270 140 Q300 115 330 100 T400 75" stroke="url(#barGrad)" strokeWidth="2.5" strokeLinecap="round" fill="none" />
        <circle className="inv-fade" cx="400" cy="75" r="10" fill={`${A}15`} filter="url(#glow)" />
        <circle className="inv-fade" cx="400" cy="75" r="5" fill="url(#sphereA)" />
        <ellipse className="inv-fade" cx="398" cy="72" rx="2.5" ry="1.8" fill="rgba(255,255,255,0.5)" />

        {/* Sparkline at bottom with gradient */}
        <path className="inv-fade" d="M264 270 L285 262 L306 265 L327 255 L348 258 L369 250 L390 245 L411 240" stroke="url(#barGrad)" strokeWidth="2" strokeLinecap="round" fill="none" />
        <circle className="inv-fade" cx="411" cy="240" r="4" fill="url(#sphereP)" />
        <ellipse className="inv-fade" cx="410" cy="238" rx="1.8" ry="1.2" fill="rgba(255,255,255,0.5)" />

        {/* Decorative floating ring */}
        <g className="inv-fade inv-float" style={{ animationDelay: '1.5s' }}>
          <ellipse cx="340" cy="55" rx="18" ry="7" fill="none" stroke="url(#barGrad)" strokeWidth="2" opacity="0.5" />
          <ellipse cx="340" cy="55" rx="12" ry="4.5" fill="none" stroke={`${P}25`} strokeWidth="1" />
        </g>
      </svg>
    </Box>
  );
};

const features = [
  { title: 'Live stock levels across all storage points', color: P },
  { title: 'Auto-deduction as orders flow in', color: P },
  { title: 'Smart reorder alerts before you run low', color: A },
  { title: 'Demand prediction based on consumption patterns', color: P },
  { title: 'Historical analysis — weekdays vs weekends, lunch vs dinner', color: A },
  { title: 'External factors: weather, events, seasonal trends', color: P },
];

export const InventorySection = () => (
  <ColorBlock bg={BLOCKS.lavender} innerBg="white" innerRadius="32px">
    {/* Header */}
    <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
      <SectionHeading size="lg" align="center" sx={{ color: PD, mb: 2 }}>
        Know <Box component="span" sx={{ fontFamily: "'Instrument Serif', serif", fontWeight: 700, fontStyle: 'italic' }}>exactly</Box> what you have.
        <br />
        Know <Box component="span" sx={{ fontFamily: "'Instrument Serif', serif", fontWeight: 700, fontStyle: 'italic' }}>exactly</Box> what you need.
      </SectionHeading>
    </Box>

    {/* SVG + features */}
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 5, md: 6 }, alignItems: 'center' }}>
      <Box sx={{ flex: '1 1 50%', minWidth: 0 }}>
        <InventorySVG />
      </Box>
      <Box sx={{ flex: '1 1 50%', minWidth: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {features.map((f, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: f.color, opacity: 0.7, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.9rem', color: 'rgba(0,0,0,0.7)', lineHeight: 1.6 }}>
                {f.title}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  </ColorBlock>
);
